import React from 'react'
import { postInsightFeedback } from '@/features/InsightPanel/api'
import { PANEL_STATES } from '@/features/InsightPanel/constants'
import type {
  DefaultReasons,
  InsightFeedbackHookResponse,
  PanelStates,
} from '@/features/InsightPanel/types'
import { useCurrentQueryId, useInsightResults } from '@/stores/results-store'
import { captureEvent, POSTHOG_EVENTS } from '@/utils/posthog'
import { singletonHook } from 'react-singleton-hook'

function useInsightFeedbackImpl(): InsightFeedbackHookResponse {
  const insightId = useInsightResults()[0].id
  const queryId = useCurrentQueryId()!

  const [selectedReasons, setSelectedReasons] = React.useState<
    DefaultReasons[]
  >([])
  const [customReason, setCustomReason] = React.useState<string | null>(null)

  const [isOpen, setIsOpen] = React.useState(false)
  const [isSubmitted, setIsSubmitted] = React.useState(false)
  const [isCustomReasonOpen, setIsCustomReasonOpen] = React.useState(false)

  function openPanel() {
    captureEvent(POSTHOG_EVENTS.FEEDBACK.INSIGHT_OPENED)
    setIsOpen(true)
  }

  // Close the panel and reset the component state
  function closePanel() {
    captureEvent(POSTHOG_EVENTS.FEEDBACK.INSIGHT_CLOSED)
    setIsOpen(false)
    setSelectedReasons([])
    setCustomReason(null)
    setIsSubmitted(false)
  }

  function handleOpenCustomReason() {
    setIsCustomReasonOpen(true)
  }
  function handleCloseCustomReason() {
    setIsCustomReasonOpen(false)
    setCustomReason(null)
  }

  async function submit(event: React.FormEvent) {
    event.preventDefault()
    const payload = {
      queryId,
      id: insightId,
      feedback: selectedReasons,
      custom: customReason,
    }
    captureEvent(POSTHOG_EVENTS.FEEDBACK.INSIGHT_SUBMITTED, {
      ...payload,
      feedback: JSON.stringify(payload.feedback),
    })
    await postInsightFeedback(payload)
    setIsSubmitted(true)
    setIsOpen(false)
  }

  function handleSelectReason(value: string[]) {
    setSelectedReasons(value as DefaultReasons[])
  }

  const panelState: PanelStates = isSubmitted
    ? PANEL_STATES.DONE
    : isOpen
      ? PANEL_STATES.PANEL
      : PANEL_STATES.BUTTON

  return {
    panelState,
    selectedReasons,
    openPanel,
    closePanel,
    handleSelectReason,
    handleSubmit: (event: React.FormEvent) => {
      void submit(event)
    },
    customReason: {
      value: customReason,
      isOpen: isCustomReasonOpen,
      handleOpen: handleOpenCustomReason,
      handleClose: handleCloseCustomReason,
      handleChange: (event: React.ChangeEvent<HTMLInputElement>) => {
        setCustomReason(event.currentTarget.value)
      },
    },
  }
}

export const useInsightFeedback = singletonHook(
  () => ({
    panelState: PANEL_STATES.BUTTON,
    selectedReasons: [],
    openPanel: () => {},
    closePanel: () => {},
    handleSubmit: () => {},
    handleSelectReason: () => {},
    customReason: {
      value: null,
      isOpen: false,
      handleOpen: () => {},
      handleClose: () => {},
      handleChange: () => {},
    },
  }),
  useInsightFeedbackImpl,
)
